import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  CssBaseline,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DesignServices as DesignServicesIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Mic as MicIcon,
  Storefront as ExhibitionIcon,
  Brightness2 as MoonIcon,
  Brightness7 as SunIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import DesignsTable from './DesignsTable';
import OwnersTable from './OwnersTable';
import TicketsTable from './TicketsTable';
import VoiceUsageTable from './VoiceUsageTable';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import './AdminPage.css';
import { useDesignStore } from 'context/DesignContext';
import { useAuth } from 'context/AuthContext';
import { hasPermission, Permissions } from 'auth/permissions';
import ExhibitionSessionsTable from './ExhibitionSessionsTable';

const drawerWidth = 240;

enum MenuItemsEnum {
  Designs = 'designs',
  Users = 'users',
  Tickets = 'tickets',
  Voice = 'voice',
  // Exhibition = 'exhibition',
}

const AdminPage: React.FC = () => {
  const { isDarkMode, toggleTheme } = useAppTheme();
  const muiTheme = useTheme();
  const isMobileNav = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<MenuItemsEnum>(MenuItemsEnum.Designs);
  const selectedDesign = useDesignStore().insoleDesign;
  const authenticatedUser = useAuth().user;
  const logout = useAuth().logout;

  const menuItems = [
    { name: MenuItemsEnum.Designs, icon: <DesignServicesIcon />, component: <DesignsTable /> },
    { name: MenuItemsEnum.Users, icon: <PersonIcon />, component: <OwnersTable /> },
    { name: MenuItemsEnum.Voice, icon: <MicIcon />, component: <VoiceUsageTable /> },
     // { name: MenuItemsEnum.Exhibition, icon: <ExhibitionIcon />, component: <ExhibitionSessionsTable /> },
    { name: MenuItemsEnum.Tickets, icon: <SupportIcon />, component: <TicketsTable /> },
  ];

  const currentUserHasTicketAccess = (): boolean => {
    if (authenticatedUser?.isAdmin) return true;
    return hasPermission(authenticatedUser, Permissions.UserViewAll)
      || hasPermission(authenticatedUser, Permissions.UserViewGroup);
  };

  const currentUserHasVoiceUsageAccess = (): boolean => {
    // Any admin who can open the admin panel may view voice usage (API scopes the data).
    return !!authenticatedUser?.isAdmin;
  };

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.name === MenuItemsEnum.Tickets && !currentUserHasTicketAccess()) return false;
    if (item.name === MenuItemsEnum.Voice && !currentUserHasVoiceUsageAccess()) return false;
    return true;
  });

  useEffect(() => {
    if (selectedDesign) setSelectedSection(MenuItemsEnum.Designs);
  }, [selectedDesign]);

  useEffect(() => {
    if (!isMobileNav) setMobileOpen(false);
  }, [isMobileNav]);

  const activeSection = visibleMenuItems.some((item) => item.name === selectedSection)
    ? selectedSection
    : MenuItemsEnum.Designs;

  const handleSectionSelect = (name: MenuItemsEnum) => {
    setSelectedSection(name);
    if (isMobileNav) setMobileOpen(false);
  };

  const drawerPaperSx = {
    width: drawerWidth,
    boxSizing: 'border-box' as const,
    backgroundColor: isDarkMode ? '#2d2d2d' : '#ffffff',
    borderRight: '1px solid var(--toolbar-border)',
    color: 'var(--text-color)',
    ...(isMobileNav
      ? { top: 0, height: '100%' }
      : { top: '64px', height: 'calc(100vh - 64px)' }),
  };

  const drawerList = (
    <>
      {!isMobileNav && <Divider sx={{ borderColor: 'var(--toolbar-border)' }} />}
      {isMobileNav && (
        <Toolbar sx={{ minHeight: { xs: 56 }, px: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isDarkMode ? '#fff' : '#076596' }}>
            Menu
          </Typography>
        </Toolbar>
      )}
      {isMobileNav && <Divider sx={{ borderColor: 'var(--toolbar-border)' }} />}

      <List>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              selected={activeSection === item.name}
              onClick={() => handleSectionSelect(item.name)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? '#076596' : '#b8d6ff',
                  '&:hover': { backgroundColor: isDarkMode ? '#0a6fb4' : '#b8d6ff' },
                },
                '&:hover': { backgroundColor: isDarkMode ? '#5a5a5a' : '#becfe6' },
                color: isDarkMode ? '#ffffff' : '#076596',
              }}
            >
              <ListItemIcon sx={{ color: isDarkMode ? '#ffffff' : '#076596', minWidth: '40px' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  item.name === MenuItemsEnum.Voice
                    ? 'Voice usage'
                    : item.name === MenuItemsEnum.Tickets
                      ? 'tickets'
                      : item.name
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
        <Divider sx={{ borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc' }} />
        <ListItem disablePadding>
          <ListItemButton
            onClick={logout}
            sx={{
              '&:hover': { backgroundColor: isDarkMode ? '#5a5a5a' : '#becfe6' },
              color: isDarkMode ? '#ffffff' : '#076596',
            }}
          >
            <ListItemIcon sx={{ color: isDarkMode ? '#ffffff' : '#076596', minWidth: '40px' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <Box className="admin-panel" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: isDarkMode ? '#1e1e1e' : '#f5f5f5' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'var(--accent-color)',
          color: '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          {isMobileNav && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label="open navigation menu"
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontSize: { xs: '1.1rem', sm: '1.5rem' }, fontWeight: 500 }}
          >
            Admin Panel
          </Typography>

          <IconButton
            color="inherit"
            onClick={toggleTheme}
            aria-label="toggle theme"
            className="theme-toggle"
          >
            {isDarkMode ? <SunIcon /> : <MoonIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', flexGrow: 1, mt: { xs: '56px', sm: '64px' }, minWidth: 0 }}>
        <Drawer
          variant={isMobileNav ? 'temporary' : 'permanent'}
          open={isMobileNav ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: isMobileNav ? undefined : drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
          anchor="left"
        >
          {drawerList}
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            bgcolor: isDarkMode ? '#1e1e1e' : '#f5f5f5',
            color: isDarkMode ? '#ffffff' : '#076596',
            overflow: 'auto',
            minWidth: 0,
          }}
        >
          {visibleMenuItems.find((item) => item.name === activeSection)?.component}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPage;
